import {
  BreadcrumbItem,
  type CompactPlan,
  PlanSummary,
  SinglePlan,
  type UpdatePlan
} from 'prove-common'

export type PlansToggleValue = 'active' | 'draft' | 'deactivated' | 'all'

const directPlanFields = [
  'directDue',
  'directContractRef',
  'directWorkspaceId',
  'directPurposeId'
] as const

type DirectPlanField = (typeof directPlanFields)[number]

const getIncompleteDirectPlanFields = (value: unknown): DirectPlanField[] => {
  if (value === null || typeof value !== 'object') {
    return [...directPlanFields]
  }

  const plan = value as Record<string, unknown>
  return directPlanFields.filter(
    (field) =>
      !Object.prototype.hasOwnProperty.call(plan, field) ||
      plan[field] === undefined
  )
}

/** Checks the direct-field contract without narrowing to a complete SinglePlan. */
export const hasDirectPlanValues = (value: unknown): boolean =>
  getIncompleteDirectPlanFields(value).length === 0

/** Identifies full-plan-only response structure, independently of direct values. */
export const isFullPlanShape = (value: CompactPlan | SinglePlan): boolean =>
  directPlanFields.some((field) => field in value) ||
  'contractRef' in value ||
  'samplingInstructions' in value ||
  value.parents.some((parent) => typeof parent === 'object') ||
  value.children.some((child) => typeof child === 'object') ||
  value.tasks.some((task) => 'analysisType' in task)

export const buildUpdatePlanBody = (
  plan: SinglePlan,
  overrides: Partial<UpdatePlan> = {}
): UpdatePlan => {
  const incompleteFields = getIncompleteDirectPlanFields(plan)
  if (incompleteFields.length > 0) {
    throw Object.assign(
      new Error(
        'Kunne ikke oppdatere planen fordi noen planopplysninger mangler. Hent planen på nytt og prøv igjen.'
      ),
      { incompleteFields }
    )
  }

  return {
    ownerId: plan.ownerId,
    due: plan.directDue,
    description: plan.description,
    contractRef: plan.directContractRef,
    status: plan.status,
    contractId: plan.contractId,
    okProgramId: plan.okProgramId,
    workspaceId: plan.directWorkspaceId,
    fagseksjonWorkspaceId: plan.fagseksjonWorkspaceId,
    purposeId: plan.directPurposeId,
    samplingInstructions: plan.samplingInstructions,
    storageInstructions: plan.storageInstructions,
    labelingInstructions: plan.labelingInstructions,
    shippingInstructions: plan.shippingInstructions,
    followUpInstructions: plan.followUpInstructions,
    ...overrides
  }
}

export const filterPlans = (
  plans: CompactPlan[] | undefined,
  searchText: string | undefined,
  selectedOwnerIds: string[],
  status: PlansToggleValue,
  selectedPurposeId = 'all'
): CompactPlan[] => {
  if (!plans) return []

  const lowerCaseSearchText = searchText?.toLowerCase() ?? ''
  return plans.filter((plan) => {
    const matchesOwner =
      selectedOwnerIds.length === 0 ||
      selectedOwnerIds.includes(plan.ownerId ?? '')
    const matchesSearch =
      searchText == null
        ? true
        : (plan.description ?? '').toLowerCase().includes(lowerCaseSearchText)
    const matchesStatus = plan.status === status || status === 'all'
    const matchesPurpose =
      selectedPurposeId === 'all' ||
      plan.purposeId?.toString() === selectedPurposeId
    return matchesOwner && matchesSearch && matchesStatus && matchesPurpose
  })
}

export const getParentBreadcrumbs = (
  plan: SinglePlan | CompactPlan,
  getPlanPath: (planId: number) => string = (planId) => `/plans/${planId}`
): BreadcrumbItem[] => {
  const parents = plan.parents ?? []
  if (parents.length > 1) {
    throw new Error(
      'Denne planen har flere enn én forelder, noe som ikke støttes.'
    )
  }
  if (parents.length === 0) return []
  const parent = parents[0]
  return typeof parent === 'object'
    ? [
        ...getParentBreadcrumbs(parent, getPlanPath),
        { label: parent.description ?? '', to: getPlanPath(parent.id) }
      ]
    : [{ label: '...' }]
}

export const buildAncestorChain = (
  planId: number,
  planMap: Map<number, PlanSummary>,
  maxDepth = 50
): PlanSummary[] => {
  const chain: PlanSummary[] = []
  let current = planMap.get(planId)
  const visited = new Set<number>()
  let depth = 0
  while (current && !visited.has(current.id) && depth < maxDepth) {
    chain.push(current)
    visited.add(current.id)
    const parentId = current.parents?.[0]
    current = parentId ? planMap.get(parentId) : undefined
    depth++
  }
  return chain.reverse() // root -> ... -> leaf
}

export const formatFullPlanLabel = (
  planId: string,
  planMap: Map<number, PlanSummary>,
  arrow = ' → '
): string => {
  const num = Number(planId)
  if (Number.isNaN(num)) return planId
  const chain = buildAncestorChain(num, planMap)
  return chain.map((p) => p.name).join(arrow) || planId
}

export const pruneSubPlans = (
  parentIds: string[],
  currentSubs: string[],
  allPlans: PlanSummary[]
): string[] => {
  const planMap = new Map(allPlans.map((p) => [p.id, p]))
  const parentNums = parentIds.map(Number).filter((n) => !Number.isNaN(n))
  return currentSubs.filter((sid) => {
    const num = Number(sid)
    if (Number.isNaN(num)) return false
    const chain: number[] = []
    let cur = planMap.get(num)
    const visited = new Set<number>()
    while (cur && !visited.has(cur.id)) {
      chain.push(cur.id)
      visited.add(cur.id)
      const pid = cur.parents?.[0]
      cur = pid ? planMap.get(pid) : undefined
    }
    return parentNums.some((pid) => chain.includes(pid))
  })
}
